import * as React from 'react';
import { art, asian, collectibles, fashion, furniture, home, jewelry, memorabilia } from './megaNavLinks';
import { getMegaNavImageUrlLarge, getMegaNavImageUrlSmall } from '@/utils/urls';
import FindAuctions from './FindAuctions';
import { isUiMobile } from '@/redux/modules/browser';
import LinkContainer from './LinkContainer';
import MegaNavLink from './MegaNavLink';
import noop from '@/utils/noop';
import { Ref } from '@/types/Ref';
import styles from './Navigation.module.scss';
import { useSelector } from 'react-redux';

const Navigation = () => {
    const targetRef: Ref = React.useRef();
    const [active, setActive] = React.useState(0);
    const mobile = useSelector(isUiMobile);

    const isPhone = mobile && mobile !== 'tablet';

    const openNav = React.useCallback((key: number) => {
        return () => {
            setActive(key);
        };
    }, []);

    const closeNav = React.useCallback(() => {
        if (active) {
            setActive(0);
        }
    }, [active]);

    const tabletNavigation = React.useCallback(
        (link: string, key: number) => {
            return () => {
                if (active !== key) {
                    setActive(key);
                } else {
                    window.location.assign(link);
                    setActive(0);
                }
            };
        },
        [active]
    );

    const handleClickOutside = React.useCallback(
        (event: any) => {
            if (mobile === 'tablet') {
                if (targetRef && targetRef.current.contains && !targetRef.current.contains(event.target)) {
                    closeNav();
                }
            }
        },
        [closeNav, mobile]
    );

    React.useEffect(() => {
        window.addEventListener('touchstart', handleClickOutside, false);
        return () => {
            window.removeEventListener('touchstart', handleClickOutside, false);
        };
    }, [handleClickOutside]);

    return (
        <div className={styles.wrapper} ref={(ref) => (targetRef.current = ref)}>
            <div className={styles.navigation} onMouseLeave={!mobile ? closeNav : noop}>
                <div className={styles['scroll-container']}>
                    <FindAuctions />
                    <div className={`${styles.item} ${styles.pipe}`}>|</div>
                    <div className={styles.navBar}>
                        <MegaNavLink
                            active={active === 1}
                            closeNav={closeNav}
                            link="/c/art/1/"
                            mobile={mobile}
                            msgId="navigation.art"
                            openNav={openNav(1)}
                            tabletNavigation={tabletNavigation('/c/art/1/', 1)}
                        />
                        <MegaNavLink
                            active={active === 2}
                            closeNav={closeNav}
                            link="/c/jewelry/6/"
                            mobile={mobile}
                            msgId="navigation.jewelry"
                            openNav={openNav(2)}
                            tabletNavigation={tabletNavigation('/c/jewelry/6/', 2)}
                        />
                        <MegaNavLink
                            active={active === 3}
                            closeNav={closeNav}
                            link="/c/asian/158/"
                            mobile={mobile}
                            msgId="navigation.asian_antiques"
                            openNav={openNav(3)}
                            tabletNavigation={tabletNavigation('/c/asian/158/', 3)}
                        />
                        <MegaNavLink
                            active={active === 4}
                            closeNav={closeNav}
                            link="/c/furniture/4/"
                            mobile={mobile}
                            msgId="navigation.furniture"
                            openNav={openNav(4)}
                            tabletNavigation={tabletNavigation('/c/furniture/4/', 4)}
                        />
                        <MegaNavLink
                            active={active === 5}
                            closeNav={closeNav}
                            link="/c/collectibles/2/"
                            mobile={mobile}
                            msgId="navigation.collectibles"
                            openNav={openNav(5)}
                            tabletNavigation={tabletNavigation('/c/collectibles/2/', 5)}
                        />
                        <MegaNavLink
                            active={active === -1}
                            closeNav={closeNav}
                            link="/c/coins-currency-stamps/24/"
                            mobile={mobile}
                            msgId="navigation.coins"
                            openNav={openNav(-1)}
                            tabletNavigation={tabletNavigation('/c/coins-currency-stamps/24/', 0)}
                        />
                        <MegaNavLink
                            active={active === 6}
                            closeNav={closeNav}
                            link="/c/memorabilia/26429/"
                            mobile={mobile}
                            msgId="navigation.memorabilia"
                            openNav={openNav(6)}
                            tabletNavigation={tabletNavigation('/c/memorabilia/26429/', 6)}
                        />
                        <MegaNavLink
                            active={active === 7}
                            closeNav={closeNav}
                            link="/c/home-garden/5/"
                            mobile={mobile}
                            msgId="navigation.home_and_garden"
                            openNav={openNav(7)}
                            tabletNavigation={tabletNavigation('/c/home-garden/5/', 7)}
                        />
                        <MegaNavLink
                            active={active === 8}
                            closeNav={closeNav}
                            link="/c/fashion/3/"
                            mobile={mobile}
                            msgId="navigation.fashion"
                            openNav={openNav(8)}
                            tabletNavigation={tabletNavigation('/c/fashion/3/', 8)}
                        />
                    </div>
                </div>
                {!isPhone && (
                    <div>
                        <LinkContainer
                            active={active === 1}
                            closeNav={closeNav}
                            linkArray={art}
                            mobile={mobile}
                            picLinkL={getMegaNavImageUrlLarge('art')}
                            picLinkS={getMegaNavImageUrlSmall('art')}
                        />
                        <LinkContainer
                            active={active === 2}
                            closeNav={closeNav}
                            linkArray={jewelry}
                            mobile={mobile}
                            picLinkL={getMegaNavImageUrlLarge('jewelry')}
                            picLinkS={getMegaNavImageUrlSmall('jewelry')}
                        />
                        <LinkContainer
                            active={active === 3}
                            closeNav={closeNav}
                            linkArray={asian}
                            mobile={mobile}
                            picLinkL={getMegaNavImageUrlLarge('asian')}
                            picLinkS={getMegaNavImageUrlSmall('asian')}
                        />
                        <LinkContainer
                            active={active === 4}
                            closeNav={closeNav}
                            linkArray={furniture}
                            mobile={mobile}
                        />
                        <LinkContainer
                            active={active === 5}
                            closeNav={closeNav}
                            linkArray={collectibles}
                            mobile={mobile}
                            picLinkL={getMegaNavImageUrlLarge('collectibles')}
                            picLinkS={getMegaNavImageUrlSmall('collectibles')}
                        />
                        <LinkContainer
                            active={active === 6}
                            closeNav={closeNav}
                            linkArray={memorabilia}
                            mobile={mobile}
                            picLinkL={getMegaNavImageUrlLarge('memorabilia')}
                            picLinkS={getMegaNavImageUrlSmall('memorabilia')}
                        />
                        <LinkContainer active={active === 7} closeNav={closeNav} linkArray={home} mobile={mobile} />
                        <LinkContainer
                            active={active === 8}
                            closeNav={closeNav}
                            linkArray={fashion}
                            mobile={mobile}
                            picLinkL={getMegaNavImageUrlLarge('fashion')}
                            picLinkS={getMegaNavImageUrlSmall('fashion')}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(Navigation);
